import { ApiResponse, PageContent } from '@/types/api-response';
import useSWR, { mutate } from 'swr';

// Generic hook for paginated data
export function usePaginatedData<T>(
  key: string | null,
  fetcher: () => Promise<ApiResponse<PageContent<T>>>,
  options?: {
    refreshInterval?: number;
    revalidateOnFocus?: boolean;
  }
) {
  const { data: response, error, isLoading, mutate: swrMutate } = useSWR(
    key,
    fetcher,
    {
      refreshInterval: options?.refreshInterval || 30000,
      revalidateOnFocus: options?.revalidateOnFocus ?? true,
    }
  );

  return {
    // Data
    items: response?.data?.content || [],
    totalElements: response?.data?.totalElements || 0,
    totalPages: response?.data?.totalPages || 0,
    currentPage: response?.data?.number || 0,
    pageSize: response?.data?.size || 20,
    isEmpty: response?.data?.empty ?? true,
    isFirst: response?.data?.first ?? true,
    isLast: response?.data?.last ?? true,

    // State
    isLoading,
    error,

    // Actions
    refresh: () => swrMutate(),
    mutate: swrMutate,
  };
}

// Generic hook for single item data
export function useCollectionData<T>(
  key: string | null,
  fetcher: () => Promise<ApiResponse<T>>,
  options?: {
    refreshInterval?: number;
    revalidateOnFocus?: boolean;
  }
) {
  const { data: response, error, isLoading, mutate: swrMutate } = useSWR(
    key,
    fetcher,
    {
      refreshInterval: options?.refreshInterval || 0,
      revalidateOnFocus: options?.revalidateOnFocus ?? true,
    }
  );

  return {
    // Data
    data: response?.data,

    // State
    isLoading,
    error,

    // Actions
    refresh: () => swrMutate(),
    mutate: swrMutate,
  };
}

// Generic mutation hook for CRUD operations
export function useMutation<TData, TVariables = any>(
  mutationFn: (variables: TVariables) => Promise<ApiResponse<TData>>,
  options?: {
    onSuccess?: (data: ApiResponse<TData>, variables: TVariables) => void;
    onError?: (error: any, variables: TVariables) => void;
    invalidateKeys?: string[];
  }
) {
  const executeAsync = async (variables: TVariables) => {
    try {
      const result = await mutationFn(variables);

      if (result.success) {
        options?.onSuccess?.(result, variables);

        // Invalidate cache keys in parallel (including paginated queries matching prefix)
        if (options?.invalidateKeys?.length) {
          await Promise.all(
            options.invalidateKeys.map((key) =>
              mutate(
                (k) =>
                  typeof k === 'string' &&
                  (k === key || k.startsWith(`${key}?`) || k.startsWith(`${key}/`))
              )
            )
          );
        }
      } else {
        options?.onError?.(result, variables);
      }

      return result;
    } catch (error) {
      options?.onError?.(error, variables);
      throw error;
    }
  };

  return {
    execute: executeAsync,
    executeAsync,
  };
}

// Utility function to build cache keys
export function buildCacheKey(base: string, params?: Record<string, any>): string {
  if (!params) return base;

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `${base}?${queryString}` : base;
}
